import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  Select, 
  Radio, 
  Modal, 
  message, 
  Popconfirm,
  Spin,
  Empty,
  Tag
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  UserOutlined,
  BankOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined
} from '@ant-design/icons';
import clientService, { ClientData } from '../../../services/Clients_service';
import axios from '../../../api/axios';
import { isValidRcs } from '../../../utils/functions/validateRCSNumber';
import { isValidPostalCode } from '../../../utils/functions/validatePostalCode';
import { fetchPostalCodeDetails } from '../../../utils/functions/fetchPostalCodeDetails';
import '../../../css/ClientManagement.css';

const { Option } = Select;

interface ClientManagementProps {
  onClientSelect?: (client: { id: string; name: string; email: string }) => void;
  onClose?: () => void;
  selectMode?: boolean;
}

const ClientManagement: React.FC<ClientManagementProps> = ({
  onClientSelect,
  onClose,
  selectMode = false
}) => {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientData | null>(null);
  const [form] = Form.useForm();
  const [cities, setCities] = useState<string[]>([]);
  const [loadingSiren, setLoadingSiren] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await clientService.getAllClients();
      setClients(data);
    } catch (error: any) {
      message.error(error.message || 'Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      if (editingClient) {
        await clientService.updateClient(editingClient.id!, values);
        message.success('Client mis à jour avec succès');
      } else {
        await clientService.createClient(values);
        message.success('Client créé avec succès');
      }
      
      await fetchClients();
      handleCloseModal();
    } catch (error: any) {
      message.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await clientService.deleteClient(id);
      message.success('Client supprimé avec succès');
      await fetchClients();
    } catch (error: any) {
      message.error(error.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (client: ClientData) => {
    setEditingClient(client);
    form.setFieldsValue(client);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingClient(null);
    form.resetFields();
    form.setFieldsValue({ client_type: 'particulier' });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    form.resetFields();
    setCities([]);
  };

  const handleClientSelect = (client: ClientData) => {
    if (onClientSelect) {
      const displayName = clientService.formatClientForDisplay(client);
      onClientSelect({
        id: client.id!.toString(),
        name: displayName,
        email: client.email
      });
    }
  };

  // Fonction pour gérer le changement du code postal
  const handlePostalCodeChange = async (value: string) => {
    if (/^\d{0,5}$/.test(value)) {
      form.setFieldValue('code_postal', value);
      
      if (isValidPostalCode(value)) {
        try {
          const postalDetails = await fetchPostalCodeDetails(value);
          const fetchedCities = postalDetails?.cities || [];
          setCities(fetchedCities);
          
          // Auto-sélectionner la première ville si disponible
          if (fetchedCities.length > 0) {
            form.setFieldValue('ville', fetchedCities[0]);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des villes:', error);
          setCities([]);
        }
      } else {
        setCities([]);
      }
    }
  };

  // Fonction pour gérer la saisie du téléphone
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Supprimer tous les espaces
    value = value.replace(/\s+/g, '');
    
    // Si commence par +, autoriser jusqu'à 15 caractères
    if (value.startsWith('+')) {
      if (/^\+[0-9]{0,14}$/.test(value)) {
        form.setFieldValue('telephone', value);
      }
    } 
    // Si commence par 0, autoriser exactement 10 chiffres
    else if (value.startsWith('0')) {
      if (/^0[0-9]{0,9}$/.test(value)) {
        form.setFieldValue('telephone', value);
      }
    }
    // Sinon, accepter que des chiffres ou +
    else {
      if (/^[0-9+]{0,15}$/.test(value)) {
        form.setFieldValue('telephone', value);
      }
    }
  };

  // Fonction pour gérer la saisie du SIREN et récupérer les infos
  const handleSirenChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const siren = e.target.value;
    
    // Accepter uniquement les chiffres et limiter à 9 caractères
    if (/^\d{0,9}$/.test(siren)) {
      form.setFieldValue('siren', siren);
      
      // Si le SIREN est valide (9 chiffres), récupérer les infos
      if (isValidRcs(siren)) {
        setLoadingSiren(true);
        try {
          const response = await axios(`/rne/${siren}`);
          const nafCode = await axios(`/rne/naf/${siren}`);
          
          if (response && response.data?.data) {
            const data = response.data.data;
            const postalCode = data.postalCode;
            
            // Remplir automatiquement les champs
            form.setFieldsValue({
              raison_sociale: data.companyName || '',
              adresse: data.address || '',
              tva_intra: nafCode.data?.data?.vat_number || '',
            });
            
            // Gérer le code postal et les villes
            if (postalCode && isValidPostalCode(postalCode)) {
              await handlePostalCodeChange(postalCode);
            } else if (data.cities && data.cities.length > 0) {
              setCities(data.cities);
              form.setFieldValue('ville', data.cities[0]);
            }
            
            message.success('Informations de la société récupérées avec succès');
          }
        } catch (error: any) {
          console.error('Erreur lors de la récupération des données SIREN:', error);
          message.warning('Impossible de récupérer les informations de la société');
        } finally {
          setLoadingSiren(false);
        }
      }
    }
  };

  const clientType = Form.useWatch('client_type', form);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants: Variants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    hover: {
      y: -5,
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const modalVariants: Variants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const getCardActions = (client: ClientData) => {
    const actions = [];

    actions.push(
      <Button
        key="edit"
        type="text"
        icon={<EditOutlined />}
        onClick={() => handleEdit(client)}
        className="action-btn edit-btn"
      >
        Modifier
      </Button>
    );

    actions.push(
      <Popconfirm
        key="delete"
        title="Supprimer ce client ?"
        description="Cette action est irréversible."
        onConfirm={() => handleDelete(client.id!)}
        okText="Oui"
        cancelText="Non"
        okButtonProps={{ danger: true }}
      >
        <Button
          type="text"
          icon={<DeleteOutlined />}
          className="action-btn delete-btn"
          danger
        >
          Supprimer
        </Button>
      </Popconfirm>
    );

    return actions;
  };

  return (
    <div className="client-management">
      <motion.div
        className="client-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="client-header-content">
          <div className="client-header-left">
            <h2 className="client-header-title">
              <UserOutlined className="client-header-icon" />
              {selectMode ? 'Sélectionner un client' : 'Gestion des clients'}
            </h2>
            <p className="client-header-subtitle">
              {selectMode ? 'Choisissez un client pour la facturation' : 'Gérez vos clients pour la facturation'}
            </p>
          </div>

          <div className="client-header-actions">
            <Button 
              icon={<UserAddOutlined />}
              onClick={handleAdd}
              className="add-client-btn"
            >
              Ajouter un client
            </Button>
          </div>
        </div>
      </motion.div>

      <Spin spinning={loading && clients.length === 0}>
        <motion.div
          className="clients-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
            gap: '24px',
            gridAutoRows: '1fr'
          }}
        >
          <AnimatePresence>
            {clients.length === 0 && !loading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="empty-state"
              >
                <Empty
                  description="Aucun client trouvé"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button 
                    icon={<UserAddOutlined />}
                    onClick={handleAdd}
                    className="add-client-btn"
                  >
                    Créer votre premier client
                  </Button>
                </Empty>
              </motion.div>
            ) : (
              clients.map((client) => (
                <motion.div
                  key={client.id}
                  variants={cardVariants}
                  whileHover="hover"
                  layout
                >
                  <Card
                    className={`client-card ${client.client_type}`}
                    actions={getCardActions(client)}
                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <div className="client-card-content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div className="client-header-card">
                        <div className="client-type-icon">
                          {client.client_type === 'particulier' ? (
                            <UserOutlined />
                          ) : (
                            <BankOutlined />
                          )}
                        </div>
                        <Tag 
                          color={client.client_type === 'particulier' ? '#2A919D' : '#eb8635'}
                          className="client-type-tag"
                        >
                          {client.client_type === 'particulier' ? 'Particulier' : 'Société'}
                        </Tag>
                      </div>

                      <div className="client-main-info">
                        <h3 className="client-name">
                          {clientService.formatClientForDisplay(client)}
                        </h3>
                        
                        {client.client_type === 'societe' && client.siren && (
                          <p className="client-siren">SIREN: {client.siren}</p>
                        )}
                      </div>

                      <div className="client-details" style={{ flex: 1 }}>
                        <div className="detail-item">
                          <MailOutlined className="detail-icon" />
                          <span className="detail-text">{client.email}</span>
                        </div>
                        
                        <div className="detail-item">
                          <PhoneOutlined className="detail-icon" />
                          <span className="detail-text">{client.telephone}</span>
                        </div>
                        
                        <div className="detail-item">
                          <HomeOutlined className="detail-icon" />
                          <span className="detail-text">{client.adresse}</span>
                        </div>
                      </div>

                      {selectMode && (
                        <Button
                          block
                          className="select-client-btn"
                          onClick={() => handleClientSelect(client)}
                          style={{ marginTop: '16px' }}
                        >
                          Sélectionner ce client
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </motion.div>
      </Spin>

      <AnimatePresence>
        {isModalOpen && (
          <Modal
            title={
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="modal-title"
              >
                {editingClient ? 'Modifier le client' : 'Nouveau client'}
              </motion.div>
            }
            open={isModalOpen}
            onCancel={handleCloseModal}
            footer={null}
            width={600}
            className="client-modal"
            destroyOnClose
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                className="client-form"
              >
                <Form.Item
                  name="client_type"
                  label="Type de client"
                  rules={[{ required: true, message: 'Veuillez sélectionner le type de client' }]}
                >
                  <Radio.Group className="client-type-radio">
                    <Radio.Button value="particulier">
                      <UserOutlined /> Particulier
                    </Radio.Button>
                    <Radio.Button value="societe">
                      <BankOutlined /> Société
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>

                {clientType === 'particulier' ? (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Form.Item
                      name="civilite"
                      label="Civilité"
                      rules={[{ required: true, message: 'Veuillez sélectionner la civilité' }]}
                    >
                      <Select placeholder="Choisir la civilité" className="civilite-select">
                        <Option value="Mr">Monsieur</Option>
                        <Option value="Mme">Madame</Option>
                      </Select>
                    </Form.Item>

                    <div className="form-row">
                      <Form.Item
                        name="nom"
                        label="Nom"
                        rules={[{ required: true, message: 'Le nom est obligatoire' }]}
                        className="form-item-half"
                      >
                        <Input placeholder="Nom de famille" />
                      </Form.Item>

                      <Form.Item
                        name="prenom"
                        label="Prénom"
                        rules={[{ required: true, message: 'Le prénom est obligatoire' }]}
                        className="form-item-half"
                      >
                        <Input placeholder="Prénom" />
                      </Form.Item>
                    </div>

                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'L\'email est obligatoire' },
                        { type: 'email', message: 'Format d\'email invalide' }
                      ]}
                    >
                      <Input 
                        prefix={<MailOutlined />}
                        placeholder="adresse@email.com" 
                      />
                    </Form.Item>

                    <Form.Item
                      name="telephone"
                      label="Téléphone"
                      rules={[
                        {
                          pattern: /^(0\d{9}|\+33\d{9}|\+[1-9]\d{6,14})$/,
                          message: 'Numéro invalide. Doit être français (10 chiffres) ou européen (+33...)'
                        }
                      ]}
                    >
                      <Input 
                        prefix={<PhoneOutlined />}
                        placeholder="Numéro de téléphone"
                        maxLength={15}
                      />
                    </Form.Item>

                    <Form.Item
                      name="adresse"
                      label="Adresse"
                      rules={[{ required: true, message: 'L\'adresse est obligatoire' }]}
                    >
                      <Input 
                        prefix={<HomeOutlined />}
                        placeholder="Adresse complète"
                      />
                    </Form.Item>

                    <div className="form-row">
                      <Form.Item
                        name="code_postal"
                        label="Code Postal"
                        rules={[
                          { required: true, message: 'Le code postal est obligatoire' },
                          { len: 5, message: 'Le code postal doit contenir 5 chiffres' }
                        ]}
                        className="form-item-half"
                      >
                        <Input 
                          placeholder="Code postal (5 chiffres)"
                          onChange={(e) => handlePostalCodeChange(e.target.value)}
                          maxLength={5}
                        />
                      </Form.Item>

                      <Form.Item
                        name="ville"
                        label="Ville"
                        rules={[{ required: true, message: 'La ville est obligatoire' }]}
                        className="form-item-half"
                      >
                        <Select 
                          placeholder="Sélectionner la ville"
                          disabled={cities.length === 0}
                          notFoundContent={cities.length === 0 ? "Saisissez un code postal valide" : "Aucune ville trouvée"}
                        >
                          {cities.map((city, index) => (
                            <Option key={index} value={city}>
                              {city}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Form.Item
                      name="siren"
                      label="SIREN"
                      rules={[
                        { required: true, message: 'Le SIREN est obligatoire' },
                        { len: 9, message: 'Le SIREN doit contenir exactement 9 chiffres' }
                      ]}
                      tooltip="Saisir le SIREN pour auto-compléter les informations"
                    >
                      <Input 
                        placeholder="Numéro SIREN (9 chiffres)" 
                        onChange={handleSirenChange}
                        maxLength={9}
                        suffix={loadingSiren ? <Spin size="small" /> : null}
                      />
                    </Form.Item>

                    <Form.Item
                      name="raison_sociale"
                      label="Raison sociale"
                      rules={[{ required: true, message: 'La raison sociale est obligatoire' }]}
                    >
                      <Input placeholder="Raison sociale de l'entreprise" />
                    </Form.Item>

                    <Form.Item
                      name="tva_intra"
                      label="TVA Intracommunautaire"
                      rules={[{ required: true, message: 'La TVA intra est obligatoire' }]}
                    >
                      <Input placeholder="Numéro de TVA intra" />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'L\'email est obligatoire' },
                        { type: 'email', message: 'Format d\'email invalide' }
                      ]}
                    >
                      <Input 
                        prefix={<MailOutlined />}
                        placeholder="adresse@email.com" 
                      />
                    </Form.Item>

                    <Form.Item
                      name="telephone"
                      label="Téléphone"
                      rules={[
                        {
                          pattern: /^(0\d{9}|\+33\d{9}|\+[1-9]\d{6,14})$/,
                          message: 'Numéro invalide. Doit être français (10 chiffres) ou européen (+33...)'
                        }
                      ]}
                    >
                      <Input 
                        prefix={<PhoneOutlined />}
                        placeholder="Numéro de téléphone"
                        maxLength={15}
                      />
                    </Form.Item>

                    <Form.Item
                      name="adresse"
                      label="Adresse"
                      rules={[{ required: true, message: 'L\'adresse est obligatoire' }]}
                    >
                      <Input 
                        prefix={<HomeOutlined />}
                        placeholder="Adresse complète"
                      />
                    </Form.Item>

                    <div className="form-row">
                      <Form.Item
                        name="code_postal"
                        label="Code Postal"
                        rules={[
                          { required: true, message: 'Le code postal est obligatoire' },
                          { len: 5, message: 'Le code postal doit contenir 5 chiffres' }
                        ]}
                        className="form-item-half"
                      >
                        <Input 
                          placeholder="Code postal (5 chiffres)"
                          onChange={(e) => handlePostalCodeChange(e.target.value)}
                          maxLength={5}
                        />
                      </Form.Item>

                      <Form.Item
                        name="ville"
                        label="Ville"
                        rules={[{ required: true, message: 'La ville est obligatoire' }]}
                        className="form-item-half"
                      >
                        <Select 
                          placeholder="Sélectionner la ville"
                          disabled={cities.length === 0}
                          notFoundContent={cities.length === 0 ? "Saisissez un code postal valide" : "Aucune ville trouvée"}
                        >
                          {cities.map((city, index) => (
                            <Option key={index} value={city}>
                              {city}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </motion.div>
                )}

                <div className="form-actions">
                  <Button onClick={handleCloseModal} className="cancel-btn">
                    Annuler
                  </Button>
                  <Button 
                    htmlType="submit" 
                    loading={loading}
                    icon={<SaveOutlined />}
                    className="save-btn"
                  >
                    {editingClient ? 'Mettre à jour' : 'Créer le client'}
                  </Button>
                </div>
              </Form>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientManagement;